import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useDarkMode } from '@/hooks/useDarkMode';

// Mock notification data for badge count
const MOCK_UNREAD_COUNT = 7;

interface Profile {
  id: string;
  full_name: string;
  role: string;
  avatar_url: string | null;
  company_id: string | null;
}

interface HeaderProps {
  profile: Profile | null;
  onMenuClick: () => void;
}

export default function Header({ profile, onMenuClick }: HeaderProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(MOCK_UNREAD_COUNT);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const { signOut } = useAuth();
  const { isDark, toggle } = useDarkMode();
  const navigate = useNavigate();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleClearCache = () => {
    if (window.confirm('Isso vai limpar todo o cache local e recarregar a página. Deseja continuar?')) {
      try {
        localStorage.clear();
        sessionStorage.clear();
        if (window.indexedDB) {
          window.indexedDB.databases?.().then((dbs) => {
            dbs?.forEach((db) => {
              if (db.name) window.indexedDB.deleteDatabase(db.name);
            });
          }).catch(() => {});
        }
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name));
          }).catch(() => {});
        }
      } catch {
        // ignore
      }
      window.location.reload();
    }
  };

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '??';

  return (
    <header className="h-[68px] bg-white dark:bg-background-100 flex items-center justify-between px-5 md:px-7 flex-shrink-0 border-b border-background-200 dark:border-background-300/50">
      {/* Left */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 hover:text-foreground-700 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-menu-line text-xl" />
        </button>

        <div className="hidden sm:flex items-center gap-2.5 text-sm text-foreground-500">
          <div className="relative">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
            <input
              type="text"
              placeholder="Pesquisar... (Ctrl+K)"
              className="bg-background-50 dark:bg-background-200 border border-background-200 dark:border-background-300 hover:border-background-300 dark:hover:border-background-400 rounded-xl pl-10 pr-4 py-2 text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 w-60 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        {/* Dark mode toggle */}
        <button
          onClick={toggle}
          className="w-10 h-10 flex items-center justify-center rounded-xl text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 hover:text-foreground-700 transition-all duration-200 cursor-pointer relative"
          title={isDark ? 'Modo claro' : 'Modo escuro'}
        >
          <div className="relative w-5 h-5">
            <i
              className={`ri-moon-line text-lg absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                isDark ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'
              }`}
            />
            <i
              className={`ri-sun-line text-lg absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                isDark ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'
              }`}
            />
          </div>
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen(!notifOpen); setDropdownOpen(false); }}
            className="relative w-10 h-10 flex items-center justify-center rounded-xl text-foreground-500 hover:bg-background-100 dark:hover:bg-background-200 hover:text-foreground-700 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-notification-3-line text-xl" />
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold leading-none px-1 animate-pulse">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-[400px] bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.1)] dark:shadow-[0_12px_48px_rgba(0,0,0,0.4)] z-50 overflow-hidden animate-fadeIn">
              <div className="flex items-center justify-between px-4 py-3 border-b border-background-100 dark:border-background-200">
                <div className="flex items-center gap-2">
                  <i className="ri-notification-3-line text-red-500" />
                  <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">Notificações</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400 text-[10px] font-bold rounded-md">{unreadCount} não lidas</span>
                </div>
              </div>
              <div className="max-h-[380px] overflow-y-auto divide-y divide-background-100 dark:divide-background-200">
                {/* Internal notifications */}
                <div className="px-4 py-2 bg-background-50/50 dark:bg-background-200/30">
                  <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Alertas Recentes</p>
                </div>
                {[
                  { text: 'Campanha "Promoção de Agosto" concluída com 98.4% de entrega!', time: 'Agora', icon: 'ri-check-double-line', bg: 'bg-emerald-50 dark:bg-emerald-500/10', color: 'text-emerald-600' },
                  { text: 'Limite de mensagens: 80% utilizado este mês', time: 'Há 2h', icon: 'ri-alert-line', bg: 'bg-amber-50 dark:bg-amber-500/10', color: 'text-amber-600' },
                  { text: 'Novo chamado de suporte respondido', time: 'Há 3h', icon: 'ri-customer-service-2-line', bg: 'bg-primary-50 dark:bg-primary-500/10', color: 'text-primary-600' },
                  { text: 'Falha na campanha "Lembrete de Consulta"', time: 'Há 4h', icon: 'ri-error-warning-line', bg: 'bg-red-50 dark:bg-red-500/10', color: 'text-red-500' },
                  { text: 'Backup automático concluído com sucesso', time: 'Há 5h', icon: 'ri-archive-line', bg: 'bg-secondary-50 dark:bg-secondary-500/10', color: 'text-secondary-600' },
                  { text: 'Nova atualização: Evolution API v2.3.0 disponível', time: 'Há 6h', icon: 'ri-download-cloud-2-line', bg: 'bg-accent-50 dark:bg-accent-500/10', color: 'text-accent-600' },
                ].map((n, i) => (
                  <div key={`notif-${i}`} className="flex items-start gap-3 px-4 py-3 hover:bg-background-50 dark:hover:bg-background-200 transition-colors cursor-pointer" onClick={() => { navigate('/notifications'); setNotifOpen(false); }}>
                    <div className={`w-8 h-8 rounded-lg ${n.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <i className={`${n.icon} ${n.color} text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-foreground-800 dark:text-foreground-200 leading-snug">{n.text}</p>
                      <p className="text-[10px] text-foreground-400 mt-1">{n.time}</p>
                    </div>
                    {i < unreadCount && (
                      <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-2" />
                    )}
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 border-t border-background-100 dark:border-background-200 flex gap-2">
                <button
                  onClick={() => { navigate('/notifications'); setNotifOpen(false); }}
                  className="flex-1 px-3 py-2 bg-background-100 dark:bg-background-200 hover:bg-background-200 dark:hover:bg-background-300 text-foreground-700 dark:text-foreground-300 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-notification-3-line" /> Ver Todas
                </button>
                <button
                  onClick={() => { setUnreadCount(0); }}
                  className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-check-double-line" /> Marcar Lidas
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-background-200 dark:bg-background-300 mx-1" />

        {/* Profile dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => { setDropdownOpen(!dropdownOpen); setNotifOpen(false); }}
            className="flex items-center gap-2.5 pl-1 pr-2 py-1.5 rounded-xl hover:bg-background-100 dark:hover:bg-background-200 transition-all duration-200 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-primary-700 text-[11px] font-bold">
              {initials}
            </div>
            <div className="hidden md:block text-left leading-tight">
              <p className="text-[13px] font-semibold text-foreground-900 dark:text-foreground-100">{profile?.full_name || 'Usuário'}</p>
              <p className="text-[11px] text-foreground-500 capitalize">{profile?.role || 'admin'}</p>
            </div>
            <i className={`ri-arrow-down-s-line text-foreground-400 text-sm hidden md:block transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-60 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.1)] dark:shadow-[0_12px_48px_rgba(0,0,0,0.4)] z-50 overflow-hidden animate-fadeIn">
              <div className="px-4 py-3 border-b border-background-100 dark:border-background-200">
                <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">{profile?.full_name}</p>
                <p className="text-xs text-foreground-500 mt-0.5 capitalize">{profile?.role}</p>
              </div>
              <button
                onClick={() => { navigate('/profile'); setDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 dark:text-foreground-300 hover:bg-background-50 dark:hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
                  <i className="ri-user-settings-line text-primary-600 dark:text-primary-400" />
                </div>
                Meu Perfil
              </button>
              <button
                onClick={() => { navigate('/support'); setDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 dark:text-foreground-300 hover:bg-background-50 dark:hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
                  <i className="ri-customer-service-2-line text-emerald-600 dark:text-emerald-400" />
                </div>
                Suporte
              </button>
              <button
                onClick={() => { navigate('/subscription'); setDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 dark:text-foreground-300 hover:bg-background-50 dark:hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-accent-600 dark:text-accent-400" />
                </div>
                Planos e Assinatura
              </button>
              <button
                onClick={() => { navigate('/settings'); setDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 dark:text-foreground-300 hover:bg-background-50 dark:hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-secondary-50 dark:bg-secondary-500/10 flex items-center justify-center">
                  <i className="ri-settings-3-line text-secondary-600 dark:text-secondary-400" />
                </div>
                Configurações
              </button>
              <button
                onClick={() => { handleClearCache(); setDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 dark:text-foreground-300 hover:bg-background-50 dark:hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center">
                  <i className="ri-brush-line text-amber-600 dark:text-amber-400" />
                </div>
                Limpar cache
              </button>
              <div className="border-t border-background-100 dark:border-background-200 my-1" />
              <button
                onClick={signOut}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
                  <i className="ri-logout-box-line text-red-500 dark:text-red-400" />
                </div>
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}